import { defineMessages } from '../../i18n/index.ts'

/**
 * What a guest who has published is told, in one voice for both places that tell her: the
 * layout banner and the share sheet right after Publish. The truth they state (api retention.ts,
 * auth/routes.ts): her link keeps working and nothing of hers is deleted, but her work and her
 * students' answers open only in the browser that holds her guest session. Another device or
 * cleared site data cannot reach them, and a guest has no other way in.
 *
 * `title` and `handedIn` are the banner's headline, all a phone sees: keep each under about 45
 * characters in every language, or the note takes half of a 360px screen again. The longer truth
 * is `body`, which the banner shows from `sm` up and the sheet always shows.
 */
export const keepWorkMessages = defineMessages({
	en: {
		title: 'Only this browser can open your work.',
		// "Someone", not "A student": a try she made on her own link counts like any other.
		handedIn: 'Someone handed in. Save your work.',
		body: 'Your link keeps working, but only this browser can open your work and your students’ answers. Save with Google or email to open them on any device.',
		action: 'Save my work',
		email: 'Save with email',
	},
	ro: {
		title: 'Doar acest browser îți deschide lucrarea.',
		handedIn: 'Cineva a predat. Salvează-ți lucrul.',
		body: 'Linkul tău rămâne activ, dar doar acest browser îți poate deschide lucrarea și răspunsurile elevilor. Salvează cu Google sau cu e-mailul ca să le deschizi de pe orice dispozitiv.',
		action: 'Salvează-mi lucrul',
		email: 'Salvează cu e-mailul',
	},
	ru: {
		title: 'Работу откроет только этот браузер.',
		handedIn: 'Кто-то сдал работу. Сохраните задание.',
		body: 'Ваша ссылка продолжит работать, но задание и ответы учеников откроются только в этом браузере. Сохраните через Google или почту, чтобы открыть их на любом устройстве.',
		action: 'Сохранить работу',
		email: 'Сохранить по почте',
	},
	fr: {
		title: 'Seul ce navigateur ouvre votre travail.',
		handedIn: 'Quelqu’un a rendu. Enregistrez votre travail.',
		body: 'Votre lien continue de fonctionner, mais seul ce navigateur peut ouvrir votre travail et les réponses de vos élèves. Enregistrez avec Google ou par e-mail pour les ouvrir sur n’importe quel appareil.',
		action: 'Enregistrer mon travail',
		email: 'Enregistrer par e-mail',
	},
	it: {
		title: 'Solo questo browser apre il tuo lavoro.',
		handedIn: 'Qualcuno ha consegnato. Salva il lavoro.',
		body: 'Il tuo link continua a funzionare, ma solo questo browser può aprire il tuo lavoro e le risposte dei tuoi studenti. Salva con Google o con l’e-mail per aprirli da qualsiasi dispositivo.',
		action: 'Salva il mio lavoro',
		email: 'Salva con l’e-mail',
	},
	kk: {
		title: 'Жұмысты тек осы браузер аша алады.',
		handedIn: 'Біреу тапсырды. Жұмысты сақтаңыз.',
		body: 'Сілтемеңіз жұмыс істей береді, бірақ тапсырма мен оқушылардың жауаптарын тек осы браузер аша алады. Оларды кез келген құрылғыда ашу үшін Google немесе пошта арқылы сақтаңыз.',
		action: 'Жұмысты сақтау',
		email: 'Пошта арқылы сақтау',
	},
	es: {
		title: 'Solo este navegador abre tu trabajo.',
		handedIn: 'Alguien entregó. Guarda tu trabajo.',
		body: 'Tu enlace sigue funcionando, pero solo este navegador puede abrir tu trabajo y las respuestas de tus alumnos. Guarda con Google o con tu correo para abrirlos en cualquier dispositivo.',
		action: 'Guardar mi trabajo',
		email: 'Guardar con correo',
	},
	pt: {
		title: 'Só este navegador abre seu trabalho.',
		handedIn: 'Alguém entregou. Salve seu trabalho.',
		body: 'Seu link continua funcionando, mas só este navegador pode abrir seu trabalho e as respostas dos seus alunos. Salve com o Google ou com o e-mail para abri-los em qualquer dispositivo.',
		action: 'Salvar meu trabalho',
		email: 'Salvar com e-mail',
	},
	de: {
		title: 'Nur dieser Browser öffnet deine Arbeit.',
		handedIn: 'Jemand hat abgegeben. Jetzt speichern.',
		body: 'Dein Link funktioniert weiter, aber nur dieser Browser kann deine Arbeit und die Antworten deiner Schüler öffnen. Speichere mit Google oder per E-Mail, um sie auf jedem Gerät zu öffnen.',
		action: 'Arbeit speichern',
		email: 'Per E-Mail speichern',
	},
})
